import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Indent,
  Outdent,
} from "lucide-react";
import { useEffect, useRef } from "react";

const ToolbarButton = ({ onClick, isActive, icon: Icon, title, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`p-1.5 rounded-sm transition-colors ${disabled
        ? "opacity-50 cursor-not-allowed text-zinc-300"
        : isActive
          ? "bg-zinc-100 text-black"
          : "text-zinc-500 hover:bg-zinc-100"
      }`}
    title={title}
  >
    <Icon className="size-4" />
  </button>
);

const TiptapEditor = ({ value, onChange, className, readOnly = false }) => {
  const isTyping = useRef(false);

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || "",
    editable: !readOnly,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      console.log("description html", editor);

      if (onChange && html !== value) {
        isTyping.current = true;
        onChange(html);
        setTimeout(() => {
          isTyping.current = false;
        }, 100);
      }
      // onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm focus:outline-none w-full !max-w-none text-sm ${!readOnly ? "min-h-[150px] p-3" : ""}`,
      },
      handleKeyDown: (view, event) => {
        // Prevent default space behavior that might cause cursor jump
        if (event.key === " " && !readOnly) {
          // Let TipTap handle the space normally
          return false;
        }
        return false;
      },
      handlePaste: (view, event) => {
        if (readOnly) return false;
        const items = Array.from(event.clipboardData?.items || []);
        const hasImage = items.some((item) => item.type.indexOf("image") === 0);

        if (hasImage) {
          event.preventDefault();
          return true; // Prevent default paste behavior for images
        }
        return false; // Allow default behavior for text
      },
    },
  });

  const previousValue = useRef(value);

  useEffect(() => {
    // Don't update content if user is actively typing
    if (editor && value !== previousValue.current && !isTyping.current) {
      previousValue.current = value;
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={`${className} ${!readOnly ? "border border-zinc-200 rounded-md bg-white overflow-hidden flex flex-col" : ""}`}
    >
      <EditorContent editor={editor} className="flex-1 overflow-auto" />

      {!readOnly && (
        <div className="flex items-center gap-1 p-2 border-t border-zinc-200 bg-zinc-50">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            icon={Bold}
            title="Bold"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            icon={Italic}
            title="Italic"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive("underline")}
            icon={UnderlineIcon}
            title="Underline"
          />
          <div className="w-px h-4 bg-zinc-300 mx-1" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            icon={List}
            title="Bullet List"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            icon={ListOrdered}
            title="Ordered List"
          />
          <div className="w-px h-4 bg-zinc-300 mx-1" />
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().liftListItem("listItem").run()
            }
            disabled={!editor.can().liftListItem("listItem")}
            icon={Outdent}
            title="Outdent"
          />
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().sinkListItem("listItem").run()
            }
            disabled={!editor.can().sinkListItem("listItem")}
            icon={Indent}
            title="Indent"
          />
        </div>
      )}
    </div>
  );
};

export default TiptapEditor;
